import React, { FC, useEffect, useMemo } from 'react'
import deepEqual from 'react-fast-compare'
import { Button, Drawer, Well } from '@pushly/aqe/lib/components'
import { EditOutlined, InfoCircleOutlined, WarningOutlined } from '@ant-design/icons'
import moment from 'moment-timezone'
import { Container } from 'typescript-ioc/es5'
import { Link } from 'react-router-dom'
import * as randomstring from 'randomstring'
import { DomainDto } from '../../dtos/domain'
import { AppState } from '../../stores/app'
import { AppService, DomainService } from '../../services'
import { getClassNames } from '../../_utils/classnames'
import { isDeliveryChannelEnabled, prepareWhitelistDomains } from '../../_utils/domain'
import { escapeRegExp, simpleFormErrorNotification } from '../../_utils/utils'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT, FEAT_SDK_EVENT_ONLY_DOMAINS } from '../../constants'
import { getSupportedTimezones } from '../../_utils/moment'
import StackedTagSelect from '../stacked-tag-select/stacked-tag-select'
import colorConvert from 'color-convert'
import ColorPickerPopover from '../color-picker-popover/color-picker-popover'
import { AbilityAction } from '../../enums/ability-action.enum'
import { asCaslSubject, CurrentUserCan } from '../../stores/app-ability'
import { SubjectEntity } from '../../enums/ability-entity.enum'
import { DeliveryChannel } from '@pushly/aqe/lib/enums/delivery-channels'
import { Form, Input, Select, Skeleton, FormProps, Space, Typography, Divider, ConfigProvider, Flex } from 'antd'
import { useForm } from 'antd/es/form/Form'
import { useService } from 'src/hooks/use-service'
import { SETTINGS_WELL_FORM_LAYOUT } from '../../theme/constants'
import dayjs from 'dayjs'

interface IDomainDetailsEditor extends FormProps {
    domain: DomainDto
    visible: boolean
    onClose: (e: any) => void
    onDomainUpdated: (domain: Partial<DomainDto>) => void
}

interface IDDEState {
    prefix: string
    color: any
    textColor: string
}

type DomainDetailsFormValues = {
    prefix?: string
    displayName: string
    overlayColor?: string | null
    timezone?: string
    whitelistDomains: string[]
    sdkEventOnlyDomains: string[]
}

export const DomainDetailsEditor: FC<any> = ({ domain, ...props }: IDomainDetailsEditor) => {
    const appState = useService(AppState)
    const appService = useService(AppService)
    const domainService = useService(DomainService)

    const [form] = useForm<DomainDetailsFormValues>()

    const timezones = getSupportedTimezones()
    const suggestedTimezone = moment.tz.guess()

    const initialValues = useMemo<DomainDetailsFormValues>(() => {
        return {
            displayName: domain?.displayName,
            prefix: domain?.displayMeta?.prefix ?? 'TEST',
            overlayColor: domain?.displayMeta?.overlay_config?.color ?? '#ffbf00',
            timezone: domain?.timezone ?? suggestedTimezone,
            whitelistDomains: domain?.whitelistDomains || [],
            sdkEventOnlyDomains: domain?.sdkEventOnlyDomains || [],
        }
    }, [domain, suggestedTimezone])

    const watchedValues = Form.useWatch([], form)

    const prefix = watchedValues?.prefix ?? initialValues.prefix
    const color = watchedValues?.overlayColor ?? initialValues.overlayColor ?? '#ffbf00'
    const textColor = color && colorConvert.hex.hsl(color)?.[2] > 70 ? 'black' : 'white'

    useEffect(() => {
        form.resetFields()
        form.setFieldsValue(initialValues)
    }, [form, initialValues])

    let displayName = domain.displayName

    if (domain?.prodDomainId !== null && prefix) {
        const rgx = new RegExp(`^\\(${escapeRegExp(prefix)}\\)\\s`, 'i')
        displayName = displayName.replace(rgx, '')

        initialValues.displayName = displayName
    }

    const appUserIsInternalDomainEditor = () => {
        return appState.abilityStore.acs.isInternalEditorUserScopeForDomain(domain.id)
    }

    const eventOnlyFeatFlag = appState.flags.findActive(FEAT_SDK_EVENT_ONLY_DOMAINS)?.getKey()
    const domainHasEventOnlyDomainsFlag = !!eventOnlyFeatFlag && (domain.flags.includes(eventOnlyFeatFlag) ?? false)

    const hasChanges = useMemo(() => {
        const currentValues = {
            ...initialValues,
            ...watchedValues,
            whitelistDomains: watchedValues?.whitelistDomains || [],
            sdkEventOnlyDomains: watchedValues?.sdkEventOnlyDomains || [],
        }

        return !deepEqual(currentValues, initialValues)
    }, [initialValues, watchedValues])

    const getCurrentValues = (): Partial<DomainDto> => {
        const formValues = form.getFieldsValue(true)
        const values: Partial<DomainDto> = {}

        // display_meta update, should contain a prefix regardless of update
        let displayMeta = { ...(domain.displayMeta ?? {}) }

        if (
            formValues.overlayColor !== domain.displayMeta?.overlay_config?.color &&
            formValues.overlayColor !== undefined
        ) {
            displayMeta = {
                ...displayMeta,
                overlay_config: {
                    color: formValues.overlayColor,
                },
            }
        }

        if (!!formValues.prefix) {
            displayMeta = {
                ...displayMeta,
                prefix: formValues.prefix,
            }
        }

        if (!deepEqual(displayMeta, domain.displayMeta ?? {})) {
            values.displayMeta = displayMeta
        }

        if (appUserIsInternalDomainEditor()) {
            if (formValues.displayName !== domain.displayName) {
                values.displayName = formValues.displayName
            }
        }

        if (!deepEqual(formValues.whitelistDomains, domain.whitelistDomains || [])) {
            values.whitelistDomains = formValues.whitelistDomains
        }

        if (domainHasEventOnlyDomainsFlag) {
            if (!deepEqual(formValues.sdkEventOnlyDomains, domain.sdkEventOnlyDomains)) {
                values.sdkEventOnlyDomains = formValues.sdkEventOnlyDomains
            }
        }

        return values
    }

    const handleSubmit = async () => {
        try {
            await form.validateFields()
            const values = getCurrentValues()

            if (values.whitelistDomains) {
                values.whitelistDomains = prepareWhitelistDomains(values.whitelistDomains)
            }
            if (values.sdkEventOnlyDomains) {
                values.sdkEventOnlyDomains = prepareWhitelistDomains(values.sdkEventOnlyDomains)
            }

            const { ok, data } = await domainService.updateDomainById(domain.id, values, `patch-domain-${domain.id}`)

            if (ok) {
                props.onDomainUpdated(data)
                props.onClose(null)
            }
        } catch (err) {
            simpleFormErrorNotification(err)
        }
    }

    const isTestDomain = domain.prodDomainId

    const DisplayNameWrapper = isTestDomain
        ? ({ children }) => (
              <Space.Compact size="small" block>
                  {children}
              </Space.Compact>
          )
        : ({ children }) => <>{children}</>
    return (
        <Drawer
            getContainer={appService.getAppContainer}
            className={getClassNames('ddf-details-editor')}
            title="Update Details"
            placement="right"
            closable={true}
            onSubmit={handleSubmit}
            disableSubmit={!hasChanges}
            onClose={props.onClose}
            open={props.visible}
            width={800}
        >
            <Form
                form={form}
                initialValues={initialValues}
                {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT}
                requiredMark={false}
                size="small"
                labelAlign="left"
            >
                {appUserIsInternalDomainEditor() && (
                    <Well title="Domain Information" mode="ghost" hideFooter={true} flush>
                        <Form.Item label="Display Name" className={getClassNames('ddf-details-editor-name')} required>
                            <DisplayNameWrapper>
                                {isTestDomain && (
                                    <Form.Item
                                        name="prefix"
                                        noStyle
                                        rules={[
                                            {
                                                required: true,
                                                message: 'Display name is required.',
                                            },
                                        ]}
                                    >
                                        <Select
                                            options={[
                                                { value: 'TEST', label: 'Test' },
                                                { value: 'DEV', label: 'Dev' },
                                                { value: 'QA', label: 'QA' },
                                            ]}
                                            style={{ width: 150 }}
                                        />
                                    </Form.Item>
                                )}

                                <Form.Item
                                    name="displayName"
                                    noStyle
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Display name is required.',
                                        },
                                    ]}
                                >
                                    <Input size="small" disabled={true} />
                                </Form.Item>
                            </DisplayNameWrapper>
                        </Form.Item>

                        {domain.prodDomainId && (
                            <Form.Item
                                label="Icon Overlay Color"
                                className={getClassNames('ddf-details-editor-overlay')}
                            >
                                <Space align="start">
                                    <Form.Item name="overlayColor" noStyle valuePropName="color">
                                        <ColorPickerPopover
                                            defaultColor={color}
                                            color={color}
                                            onChange={(hex) => {
                                                form.setFieldValue('overlayColor', hex)
                                            }}
                                        />
                                    </Form.Item>
                                    <div className="generate-test-confirm-group">
                                        <div className="color-selections">
                                            <svg
                                                className="overlay"
                                                id="overlay"
                                                viewBox="0 0 400 400"
                                                width="64"
                                                height="64"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <polygon
                                                    points="0 0 400 0 400 400 0 400"
                                                    strokeWidth="30"
                                                    stroke={color}
                                                    fill="none"
                                                />
                                                <polygon
                                                    points="0 0 300 0 0 300"
                                                    strokeWidth="5"
                                                    stroke={color}
                                                    fill={color}
                                                />
                                                <text
                                                    fill={textColor}
                                                    fontSize="80"
                                                    fontWeight="900"
                                                    textAnchor="middle"
                                                    x="0"
                                                    y="200"
                                                    transform="rotate(-45)"
                                                >
                                                    {prefix}
                                                </text>
                                            </svg>
                                            {domain?.defaultIconUrl ? (
                                                <img className="overlay-icon" src={domain?.defaultIconUrl} />
                                            ) : (
                                                <span className="icon-missing">No Icon</span>
                                            )}
                                        </div>
                                    </div>
                                </Space>
                            </Form.Item>
                        )}
                    </Well>
                )}

                <Divider className="inner-well-divider" />
                <Well title="Platform Configuration" mode="ghost" footerMode="hidden" padding="none">
                    {!domain.timezone && (
                        <Form.Item
                            name="timezone"
                            rules={[
                                {
                                    required: true,
                                    message: 'Time Zone must be selected',
                                },
                            ]}
                            label="Time Zone"
                            className={getClassNames('ddf-details-editor-timezone')}
                        >
                            <Select size="small" showSearch={true} placeholder="Select a time zone">
                                {timezones.map((tz: string) => (
                                    <Select.Option key={tz} value={tz}>
                                        {tz}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}

                    <Form.Item label="SDK Allowed Domains" className={getClassNames('ddf-details-editor-whitelist')}>
                        <Flex vertical>
                            <Form.Item name="whitelistDomains" noStyle>
                                <StackedTagSelect<string[]>
                                    size="small"
                                    mode="tags"
                                    className="domains-allowed"
                                    placeholder="www.pushly.com, subdomain.pushly.com"
                                    getPopupContainer={appService.getAppContainer}
                                    tokenSeparators={[',', ';', ' ', '\n']}
                                    showOptions={false}
                                    notFoundContent=""
                                />
                            </Form.Item>
                            <Space align="start">
                                <InfoCircleOutlined className="info-icon" />
                                <Typography.Text type="secondary" className="font-size--s">
                                    Enter each domain (including development/testing domains) that will request
                                    notification permissions or will run in event only mode.
                                </Typography.Text>
                            </Space>

                            <Space align="start">
                                <WarningOutlined className="info-icon" />
                                <Typography.Text type="secondary" className="font-size--s">
                                    <strong>Note:</strong> Wildcard <strong>*</strong> entries are not supported by
                                    Apple's APNs and will result in an invalid Safari integration.
                                </Typography.Text>
                            </Space>
                        </Flex>
                    </Form.Item>

                    {domainHasEventOnlyDomainsFlag && (
                        <Form.Item
                            label="SDK Event Only Domains"
                            className={getClassNames('ddf-details-editor-sdk-ev-only-domains')}
                        >
                            <Flex vertical>
                                <Form.Item name="sdkEventOnlyDomains" noStyle>
                                    <StackedTagSelect<string[]>
                                        size="small"
                                        mode="tags"
                                        className="domains-allowed"
                                        placeholder="www.pushly.com, subdomain.pushly.com"
                                        getPopupContainer={appService.getAppContainer}
                                        tokenSeparators={[',', ';', ' ', '\n']}
                                        showOptions={false}
                                        notFoundContent=""
                                    />
                                </Form.Item>

                                <Space align="start">
                                    <InfoCircleOutlined className="info-icon" />
                                    <Typography.Paragraph type="secondary" className="font-size--s">
                                        Enter each domain (including development/testing domains) that should{' '}
                                        <b>not request permissions</b> but still be able to send subscriber events.
                                    </Typography.Paragraph>
                                </Space>
                                <Space align="start">
                                    <WarningOutlined className="info-icon" />
                                    <Typography.Paragraph type="secondary" className="font-size--s">
                                        <b>Note:</b> All domains must also be included in the SDK Allowed Domains.
                                        Wildcard <b>*</b> entries are not supported for event only domains.
                                    </Typography.Paragraph>
                                </Space>
                            </Flex>
                        </Form.Item>
                    )}
                </Well>
            </Form>
        </Drawer>
    )
}

interface IAccountDetailsWell {
    loading?: boolean
    domain: DomainDto
    title?: string
    onDomainUpdated: (domain: Partial<DomainDto>) => void
}

interface IDWState {
    showDrawer: boolean
    drawerKey?: string
}

export class DomainDetailsWell extends React.Component<IAccountDetailsWell, IDWState> {
    public state: IDWState = {
        showDrawer: false,
    }

    protected appState: AppState

    public constructor(props: IAccountDetailsWell) {
        super(props)

        this.appState = Container.get(AppState)
    }

    public render() {
        return (
            <Well
                className="nested"
                title="Details"
                actions={
                    <Button size="small" onClick={this.handleDrawerOpen} disabled={this.props.loading}>
                        <EditOutlined />
                        <span>Edit</span>
                    </Button>
                }
                hideFooter={true}
            >
                <Skeleton loading={this.props.loading} active={true} title={false}>
                    {!this.props.loading && (
                        <>
                            <Form
                                {...SETTINGS_WELL_FORM_LAYOUT}
                                colon={false}
                                requiredMark={false}
                                labelAlign="left"
                                size="small"
                            >
                                {this.appUserIsInternalDomainEditor && this.renderOrgWell()}

                                <Divider className="inner-well-divider" />

                                {this.renderInformationWell()}

                                <Divider className="inner-well-divider" />

                                {this.renderConfigurationWell()}
                            </Form>

                            <DomainDetailsEditor
                                key={this.state.drawerKey}
                                visible={this.state.showDrawer}
                                domain={this.domain}
                                onClose={this.handleDrawerClose}
                                onDomainUpdated={this.props.onDomainUpdated}
                            />
                        </>
                    )}
                </Skeleton>
            </Well>
        )
    }

    protected get appUserIsInternalDomainEditor() {
        return this.appState.abilityStore.acs.isInternalEditorUserScopeForDomain(this.domain.id)
    }

    protected get isWebEnabledForDomain() {
        return isDeliveryChannelEnabled(this.domain, DeliveryChannel.WEB)
    }

    protected renderOrgWell() {
        return (
            <Well title="Organization" mode="ghost" hideFooter={true} padding="none">
                <Form.Item label="Org Name">
                    <Link to={`/organizations/${this.appState.currentDomain?.accountId}`}>
                        {this.appState.currentDomain?.accountName}
                    </Link>
                </Form.Item>
            </Well>
        )
    }

    protected renderInformationWell() {
        return (
            <Well title="Domain Information" mode="ghost" hideFooter={true} padding="none">
                <Form.Item label="Display Name">{this.domain.displayName}</Form.Item>

                {this.appUserIsInternalDomainEditor && <Form.Item label="Integration Type">Native</Form.Item>}

                <Form.Item label="Domain URL">
                    <a href={`https://${this.domain.name}`} target="_blank">
                        {this.domain.name}
                    </a>
                </Form.Item>

                <Form.Item label="SDK Key">{this.domain.domainKey ?? ''}</Form.Item>

                {this.appUserIsInternalDomainEditor && (
                    <>
                        <Form.Item label="Created">
                            {dayjs(this.domain.createdAt).format('LLL')}
                            <CurrentUserCan
                                do={AbilityAction.READ}
                                on={asCaslSubject(SubjectEntity.USER, { id: this.domain.createdBy })}
                            >
                                <span className="lowlight"> by </span>
                                {this.domain.createdByUsername}
                            </CurrentUserCan>
                        </Form.Item>

                        <Form.Item label="Last Updated">
                            {dayjs(this.domain.updatedAt).format('LLL')}
                            <CurrentUserCan
                                do={AbilityAction.READ}
                                on={asCaslSubject(SubjectEntity.USER, { id: this.domain.updatedBy })}
                            >
                                <span className="lowlight"> by </span>
                                {this.domain.updatedByUsername}
                            </CurrentUserCan>
                        </Form.Item>
                    </>
                )}
            </Well>
        )
    }

    protected renderConfigurationWell() {
        return (
            <Well title="Platform Configuration" mode="ghost" hideFooter={true} padding="none">
                <Form.Item label="Time Zone">
                    {!this.domain.timezone ? (
                        'None selected'
                    ) : (
                        <span>
                            {this.domain.timezone} | {moment().tz(this.domain.timezone).format('z | Z')}
                        </span>
                    )}
                </Form.Item>
                {this.appUserIsInternalDomainEditor && (
                    <Space>
                        <InfoCircleOutlined className="info-icon" />
                        <Typography.Paragraph type="secondary" className="font-size--s" style={{ marginBottom: 0 }}>
                            To update your domain’s time zone please contact your account manager.
                        </Typography.Paragraph>
                    </Space>
                )}

                {this.isWebEnabledForDomain && (
                    <>
                        <Form.Item label="SDK Allowed Domains">
                            {this.domain.whitelistDomains?.join(', ') || '*'}
                        </Form.Item>
                        <Space>
                            <WarningOutlined className="info-icon" />
                            <Typography.Paragraph type="secondary" className="font-size--s" style={{ marginBottom: 0 }}>
                                Wildcard <Typography.Text strong>*</Typography.Text> entries are not supported by
                                Apple's APNs and will result in an invalid Safari integration.
                            </Typography.Paragraph>
                        </Space>
                    </>
                )}

                {this.domainHasEventOnlyDomainsFlag && this.isWebEnabledForDomain && (
                    <>
                        <Form.Item label="SDK Event Only Domains">
                            {this.domain.sdkEventOnlyDomains.join(', ') || 'N/A'}
                        </Form.Item>
                        <Space>
                            <WarningOutlined className="info-icon" />
                            <Typography.Paragraph type="secondary" className="font-size--s" style={{ marginBottom: 0 }}>
                                All domains must also be included in the SDK Allowed Domains List. Wildcard <b>*</b>{' '}
                                entries are not supported for event only domains.
                            </Typography.Paragraph>
                        </Space>
                    </>
                )}
            </Well>
        )
    }

    protected get domain(): DomainDto {
        return this.props.domain
    }

    protected get domainHasEventOnlyDomainsFlag(): boolean {
        const eventOnlyFeatFlag = this.appState.flags.findActive(FEAT_SDK_EVENT_ONLY_DOMAINS)?.getKey()
        return !!eventOnlyFeatFlag && (this.props.domain.flags.includes(eventOnlyFeatFlag) ?? false)
    }

    protected handleDrawerOpen = async () => {
        return this.setState({ showDrawer: true, drawerKey: randomstring.generate() })
    }

    protected handleDrawerClose = async () => {
        return this.setState({ showDrawer: false })
    }
}
